import Phaser from 'phaser';
import { BuildingData, BuildingStateData, socketManager } from '../socket/SocketManager';

const PANEL_WIDTH = 280;
const SLIDE_DURATION = 200;

const TYPE_COLORS: Record<string, number> = {
  HOUSE: 0xc8a882, APARTMENT: 0x9b8b7a, SHOP: 0xf4c842,
  POLICE_STATION: 0x3a6bc4, WAREHOUSE: 0x7a5c3a, PARK: 0x4caf50,
  CASINO: 0x7b2fbe, DEAL_POINT: 0x4a4a4a, WHOLESALER: 0x8b4513,
};
const TYPE_NAMES: Record<string, string> = {
  HOUSE: 'Maison', APARTMENT: 'Appartement', SHOP: 'Commerce',
  POLICE_STATION: 'Commissariat', WAREHOUSE: 'Entrepot', PARK: 'Parc',
  CASINO: 'Casino', DEAL_POINT: 'Point de deal', WHOLESALER: 'Grossiste',
};
const BUILDING_PRICES: Record<string, number> = {
  HOUSE: 200, APARTMENT: 350, SHOP: 500, WAREHOUSE: 800, CASINO: 5000, DEAL_POINT: 100,
};
const NON_PURCHASABLE = ['POLICE_STATION', 'WHOLESALER', 'PARK'];

export class SidePanel {
  private bg: Phaser.GameObjects.Rectangle;
  private isOpen = false;
  private currentBuilding: BuildingData | null = null;
  private elements: Phaser.GameObjects.GameObject[] = [];
  private onEnterCallback: ((building: BuildingData) => void) | null = null;
  private onBuyCallback: ((building: BuildingData) => void) | null = null;
  private onCloseCallback: (() => void) | null = null;
  private panelX = 0; // current X position of the panel left edge

  constructor(private scene: Phaser.Scene) {
    const { width, height } = scene.scale;
    this.panelX = width; // offscreen
    this.bg = scene.add.rectangle(width, 0, PANEL_WIDTH, height, 0x1a1a2e, 0.95)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(1050);
  }

  open(building: BuildingData, stateData?: BuildingStateData): void {
    console.log(`[SIDEPANEL] Opening panel: ${building.name} (type=${building.type}, id=${building.id})`);
    this.currentBuilding = building;
    this.clearElements();

    const { width } = this.scene.scale;
    this.panelX = width; // start offscreen
    this.bg.setPosition(this.panelX, 0);
    this.isOpen = true;

    // Animate panel in
    this.scene.tweens.add({
      targets: this.bg,
      x: width - PANEL_WIDTH,
      duration: SLIDE_DURATION,
      ease: 'Power2',
      onUpdate: () => {
        this.panelX = this.bg.x;
        this.repositionElements();
      },
      onComplete: () => {
        this.panelX = width - PANEL_WIDTH;
        this.buildContent(building, stateData);
      },
    });
  }

  close(): void {
    if (!this.isOpen) return;
    console.log(`[SIDEPANEL] Closing panel for: ${this.currentBuilding?.name}`);
    const { width } = this.scene.scale;
    this.isOpen = false;

    this.clearElements();
    this.scene.tweens.add({
      targets: this.bg,
      x: width,
      duration: SLIDE_DURATION,
      ease: 'Power2',
      onComplete: () => {
        this.currentBuilding = null;
        this.onCloseCallback?.();
      },
    });
  }

  isVisible(): boolean { return this.isOpen; }
  getBuilding(): BuildingData | null { return this.currentBuilding; }
  onEnter(cb: (b: BuildingData) => void): void { this.onEnterCallback = cb; }
  onBuy(cb: (b: BuildingData) => void): void { this.onBuyCallback = cb; }
  onClose(cb: () => void): void { this.onCloseCallback = cb; }

  private clearElements(): void {
    for (const el of this.elements) el.destroy();
    this.elements = [];
  }

  private repositionElements(): void {
    // Elements are positioned absolutely — no repositioning needed during slide
  }

  private addText(relX: number, y: number, text: string, style: Partial<Phaser.Types.GameObjects.Text.TextStyle>): Phaser.GameObjects.Text {
    const t = this.scene.add.text(this.panelX + relX, y, text, {
      fontFamily: 'monospace', ...style,
    }).setScrollFactor(0).setDepth(1051);
    this.elements.push(t);
    return t;
  }

  private addPanelButton(relX: number, y: number, label: string, color: string, bgColor: string, onClick: () => void): Phaser.GameObjects.Text {
    const btn = this.scene.add.text(this.panelX + relX, y, label, {
      fontSize: '14px', fontFamily: 'monospace', color,
      backgroundColor: bgColor, padding: { x: 12, y: 8 },
    }).setOrigin(0.5).setScrollFactor(0).setDepth(1052)
      .setInteractive({ useHandCursor: true });

    btn.on('pointerdown', (p: Phaser.Input.Pointer) => {
      p.event.stopPropagation();
      onClick();
    });
    btn.on('pointerover', () => btn.setAlpha(0.8));
    btn.on('pointerout', () => btn.setAlpha(1));

    this.elements.push(btn);
    return btn;
  }

  private buildContent(building: BuildingData, stateData?: BuildingStateData): void {
    let y = 20;
    const px = this.panelX;

    // Close button
    const closeBtn = this.addText(PANEL_WIDTH - 30, y, 'X', { fontSize: '20px', color: '#ff4444' });
    closeBtn.setOrigin(0.5).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => this.close());

    // Name
    this.addText(15, y, building.name, {
      fontSize: '16px', color: '#ffffff', wordWrap: { width: PANEL_WIDTH - 40 },
    });
    y += 30;

    // Type badge
    const typeColor = TYPE_COLORS[building.type] ?? 0x888888;
    const typeName = TYPE_NAMES[building.type] ?? building.type;
    const badge = this.scene.add.rectangle(px + 75, y, 120, 22, typeColor, 0.8)
      .setScrollFactor(0).setDepth(1051);
    this.elements.push(badge);
    this.addText(75, y, typeName, { fontSize: '11px', color: '#ffffff' }).setOrigin(0.5);
    y += 28;

    // Owner
    const playerId = socketManager.getPlayerId();
    const isOwnedByMe = building.ownerId === playerId;
    const isOwnedByOther = !!building.ownerId && !isOwnedByMe;
    const ownerLabel = isOwnedByMe ? 'Vous' : isOwnedByOther ? 'Autre joueur' : 'Disponible';
    const ownerColor = isOwnedByMe ? '#4caf50' : isOwnedByOther ? '#ff6b6b' : '#ffb347';
    this.addText(15, y, `Proprietaire: ${ownerLabel}`, {
      fontSize: '12px', color: ownerColor,
    });
    y += 20;

    // Price
    const isPurchasable = !NON_PURCHASABLE.includes(building.type);
    if (!building.ownerId && isPurchasable) {
      this.addText(15, y, `Prix: $${BUILDING_PRICES[building.type] ?? 0}`, {
        fontSize: '12px', color: '#ffeaa7',
      });
      y += 20;
    }

    // Revenue
    if (stateData && stateData.estimatedRevenue > 0) {
      this.addText(15, y, `Revenus: $${Math.floor(stateData.estimatedRevenue)}/jour`, {
        fontSize: '12px', color: '#69db7c',
      });
      y += 20;
    }

    // Suspicion bar
    if (stateData && stateData.suspicionLevel > 0) {
      this.addText(15, y, 'Suspicion:', { fontSize: '11px', color: '#aaaaaa' });
      const susp = stateData.suspicionLevel;
      const barBg = this.scene.add.rectangle(px + 90, y + 1, 120, 10, 0x333333)
        .setOrigin(0, 0.5).setScrollFactor(0).setDepth(1051);
      const barColor = susp < 40 ? 0x4caf50 : susp < 70 ? 0xff9800 : 0xff4444;
      const barFill = this.scene.add.rectangle(px + 90, y + 1, 120 * (susp / 100), 10, barColor)
        .setOrigin(0, 0.5).setScrollFactor(0).setDepth(1051);
      this.elements.push(barBg, barFill);
      this.addText(215, y, `${Math.floor(susp)}%`, { fontSize: '10px', color: '#cccccc' });
      y += 20;
    }

    y += 10;

    // Separator
    const sep = this.scene.add.rectangle(px + PANEL_WIDTH / 2, y, PANEL_WIDTH - 30, 1, 0x444444)
      .setScrollFactor(0).setDepth(1051);
    this.elements.push(sep);
    y += 15;

    // ── BUY button ──
    if (!building.ownerId && isPurchasable) {
      const price = BUILDING_PRICES[building.type] ?? 0;
      this.addPanelButton(PANEL_WIDTH / 2, y, `[ Acheter - $${price} ]`, '#ffeaa7', '#3a3a1a', () => {
        console.log(`[SIDEPANEL] BUY clicked: ${this.currentBuilding?.name} (id=${this.currentBuilding?.id}), price=$${price}`);
        if (this.currentBuilding) this.onBuyCallback?.(this.currentBuilding);
      });
      y += 40;
    }

    // ── ENTER button ──
    const canEnter = isOwnedByMe || ['WHOLESALER', 'POLICE_STATION', 'CASINO'].includes(building.type);
    if (canEnter && building.interactable) {
      this.addPanelButton(PANEL_WIDTH / 2, y, '[ Entrer -> ]', '#4caf50', '#1a3a1a', () => {
        console.log(`[SIDEPANEL] ENTER clicked: ${this.currentBuilding?.name} (id=${this.currentBuilding?.id})`);
        if (this.currentBuilding) this.onEnterCallback?.(this.currentBuilding);
      });
      y += 40;
    }

    // ── Info details ──
    if (stateData?.businessInfo) {
      const bi = stateData.businessInfo;
      this.addText(15, y, `Ratio: ${(bi.currentRatio * 100).toFixed(0)}%`, { fontSize: '11px', color: '#69db7c' });
      y += 16;
      this.addText(15, y, `Capacite: $${bi.dailyCapacity}/jour`, { fontSize: '11px', color: '#aaaaaa' });
      y += 16;
      if (bi.isClosedUntil) {
        this.addText(15, y, 'FERME PAR LA POLICE', { fontSize: '12px', color: '#ff4444' });
        y += 16;
      }
    }

    if (stateData?.dealPointInfo) {
      const dp = stateData.dealPointInfo;
      this.addText(15, y, `Drogue: ${dp.drugType}`, { fontSize: '11px', color: '#ffb347' });
      y += 16;
      this.addText(15, y, `Prix: $${dp.pricePerUnit}/u`, { fontSize: '11px', color: '#ffffff' });
      y += 16;
      this.addText(15, y, `Stock: ${dp.stockOnSite} unites`, { fontSize: '11px', color: '#aaaaaa' });
      y += 16;
    }

    if (stateData?.wholesalerInfo) {
      const wi = stateData.wholesalerInfo;
      const names: Record<number, string> = { 1: 'Bronze', 2: 'Argent', 3: 'Or', 4: 'Platine', 5: 'Diamant' };
      this.addText(15, y, `Confiance: ${names[wi.trustLevel] ?? 'Bronze'}`, { fontSize: '11px', color: '#ffd700' });
      y += 16;
    }

    if (stateData?.growRoomInfo) {
      const gi = stateData.growRoomInfo;
      this.addText(15, y, `Plants: ${gi.currentPlants}/${gi.capacity}`, { fontSize: '11px', color: '#4caf50' });
      y += 16;
      if (gi.batches.length > 0) {
        const firstBatch = gi.batches[0];
        this.addText(15, y, `Stade: ${firstBatch.stage}`, { fontSize: '11px', color: '#ffb347' });
        y += 16;
      }
    }

    if (stateData?.stashInfo) {
      const si = stateData.stashInfo;
      this.addText(15, y, `Stock: ${si.usedCapacity}/${si.capacity}`, { fontSize: '11px', color: '#3a6bc4' });
      y += 16;
    }
  }

  getGameObjects(): Phaser.GameObjects.GameObject[] {
    return [this.bg, ...this.elements];
  }
}
